/**
 * Select Component
 *
 * 드롭다운 선택 컴포넌트
 * - 네이티브 <select> 사용 (모바일 최적화)
 * - 아이콘(이모지) 지원
 * - disabled, error 상태 지원
 */

export interface SelectOption {
  value: string
  label: string
  icon?: string
  disabled?: boolean
}

export interface SelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
}

export function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  required = false,
  disabled = false,
}: SelectProps) {
  const hasError = !!error

  // 베이스 스타일
  const baseClasses = 'w-full h-12 px-4 border rounded-lg text-base transition-colors appearance-none'

  // 에러 상태
  const borderClasses = hasError
    ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:border-gray-600 dark:focus:ring-primary-400'

  // 비활성화 상태
  const disabledClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed text-gray-500 dark:bg-gray-900 dark:text-gray-400'
    : 'bg-white dark:bg-gray-800 dark:text-gray-100'

  const selectClassName = [baseClasses, borderClasses, disabledClasses]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="w-full">
      {/* Label */}
      <label className="block mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </label>

      {/* Select Field */}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          required={required}
          className={selectClassName}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${label}-error` : undefined}
          data-error={hasError}
        >
          {/* Placeholder */}
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}

          {/* Options */}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.icon ? `${option.icon} ${option.label}` : option.label}
            </option>
          ))}
        </select>

        {/* 드롭다운 아이콘 */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      {hasError && (
        <p
          id={`${label}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}
